// Firestore-compatible data API backed by the Go server.
//
// It keeps the function names and shapes the components were written
// against (collection, doc, query, where, getDocs, addDoc, Timestamp, ...),
// so the pages did not have to be rewritten when leaving Firebase.
import { request } from "./client"

// There is a single database now; `db` is kept so call sites read the same.
export const db = Object.freeze({ type: "db" })

// --- Values -------------------------------------------------------------------

export class Timestamp {
  constructor(seconds, nanoseconds = 0) {
    this.seconds = seconds
    this.nanoseconds = nanoseconds
  }
  static fromDate(date) {
    const ms = date.getTime()
    return new Timestamp(Math.floor(ms / 1000), (ms % 1000) * 1e6)
  }
  static fromMillis(ms) {
    return Timestamp.fromDate(new Date(ms))
  }
  static now() {
    return Timestamp.fromDate(new Date())
  }
  static fromISO(iso) {
    const ms = Date.parse(iso)
    const micros = /\.(\d+)Z$/.exec(iso)?.[1]?.padEnd(9, "0").slice(0, 9)
    return new Timestamp(Math.floor(ms / 1000), micros ? Number(micros) : 0)
  }
  toDate() {
    return new Date(this.toMillis())
  }
  toMillis() {
    return this.seconds * 1000 + Math.floor(this.nanoseconds / 1e6)
  }
  isEqual(other) {
    return other instanceof Timestamp && other.seconds === this.seconds && other.nanoseconds === this.nanoseconds
  }
  valueOf() {
    return this.toMillis()
  }
  toISO() {
    const base = new Date(this.seconds * 1000).toISOString().slice(0, 19)
    return `${base}.${String(Math.floor(this.nanoseconds / 1000)).padStart(6, "0")}Z`
  }
  toJSON() {
    return this.toDate().toISOString()
  }
}

const SERVER_TIMESTAMP = Object.freeze({ __sentinel: "serverTimestamp" })
const DELETE_FIELD = Object.freeze({ __sentinel: "delete" })

export const serverTimestamp = () => SERVER_TIMESTAMP
export const deleteField = () => DELETE_FIELD

// encode converts values into the wire format: timestamps become {"$ts"}.
function encode(value) {
  if (value === SERVER_TIMESTAMP) return { $serverTimestamp: true }
  if (value === DELETE_FIELD) return { $delete: true }
  if (value instanceof Timestamp) return { $ts: value.toISO() }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : { $ts: value.toISOString() }
  }
  if (Array.isArray(value)) return value.map((v) => (v === undefined ? null : encode(v)))
  if (value && typeof value === "object") {
    const out = {}
    for (const [k, v] of Object.entries(value)) {
      if (v !== undefined) out[k] = encode(v)
    }
    return out
  }
  if (typeof value === "number" && !Number.isFinite(value)) return null
  return value
}

function decode(value) {
  if (Array.isArray(value)) return value.map(decode)
  if (value && typeof value === "object") {
    const keys = Object.keys(value)
    if (keys.length === 1 && keys[0] === "$ts") return Timestamp.fromISO(value.$ts)
    const out = {}
    for (const k of keys) out[k] = decode(value[k])
    return out
  }
  return value
}

const getPath = (obj, path) => path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj)

// --- References ---------------------------------------------------------------

const autoId = () => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"
  const bytes = crypto.getRandomValues(new Uint8Array(20))
  return Array.from(bytes, (b) => chars[b % chars.length]).join("")
}

class CollectionReference {
  constructor(name) {
    this.type = "collection"
    this.id = name
    this.path = name
  }
}

class DocumentReference {
  constructor(collection, id) {
    this.type = "document"
    this.collection = collection
    this.id = id
    this.path = `${collection}/${id}`
  }
}

export function collection(_db, ...segments) {
  if (segments.length !== 1) throw new Error("Subcollections are not supported")
  return new CollectionReference(segments[0])
}

// doc(db, "col", "id") | doc(collectionRef) | doc(collectionRef, "id")
export function doc(parent, ...segments) {
  if (parent instanceof CollectionReference) {
    return new DocumentReference(parent.id, segments[0] ?? autoId())
  }
  if (segments.length !== 2) throw new Error("doc() expects a collection name and a document id")
  return new DocumentReference(segments[0], segments[1])
}

// --- Queries ------------------------------------------------------------------

class Query {
  constructor(collection, constraints = {}) {
    this.type = "query"
    this.collection = collection
    this.where = constraints.where || []
    this.orderBy = constraints.orderBy || []
    this.limit = constraints.limit || 0
    this.startAfter = constraints.startAfter || null
  }
}

export const where = (field, op, value) => ({ kind: "where", field, op, value })
export const orderBy = (field, dir = "asc") => ({ kind: "orderBy", field, dir })
export const limit = (n) => ({ kind: "limit", n })
export const startAfter = (...args) => ({ kind: "startAfter", args })

export function query(base, ...constraints) {
  const q = base instanceof Query
    ? new Query(base.collection, { ...base, where: [...base.where], orderBy: [...base.orderBy] })
    : new Query(base.id)
  for (const c of constraints) {
    if (c.kind === "where") q.where.push({ field: c.field, op: c.op, value: c.value })
    else if (c.kind === "orderBy") q.orderBy.push({ field: c.field, dir: c.dir })
    else if (c.kind === "limit") q.limit = c.n
    else if (c.kind === "startAfter") q.startAfter = c.args
  }
  return q
}

function wireQuery(q) {
  const body = {
    collection: q.collection,
    where: q.where.map((w) => ({ ...w, value: encode(w.value) })),
    orderBy: q.orderBy,
    limit: q.limit,
  }
  if (q.startAfter) {
    const [first] = q.startAfter
    if (first instanceof DocumentSnapshot) {
      // Cursor from a document: its orderBy values plus its id as tie-breaker
      body.startAfter = { values: q.orderBy.map((o) => encode(getPath(first.data(), o.field))), id: first.id }
    } else {
      body.startAfter = { values: q.startAfter.map(encode), id: "" }
    }
  }
  return body
}

const asQuery = (ref) => (ref instanceof Query ? ref : new Query(ref.id))

// --- Snapshots ----------------------------------------------------------------

class DocumentSnapshot {
  constructor(ref, data) {
    this.ref = ref
    this.id = ref.id
    this._data = data
  }
  exists() {
    return this._data !== undefined
  }
  data() {
    return this._data
  }
  get(field) {
    return this._data === undefined ? undefined : getPath(this._data, field)
  }
}

class QuerySnapshot {
  constructor(docs) {
    this.docs = docs
    this.size = docs.length
    this.empty = docs.length === 0
  }
  forEach(cb) {
    this.docs.forEach(cb)
  }
}

// --- Reads --------------------------------------------------------------------

export async function getDoc(ref) {
  const res = await request("GET", `/db/${encodeURIComponent(ref.collection)}/${encodeURIComponent(ref.id)}`)
  return new DocumentSnapshot(ref, res.exists ? decode(res.data) : undefined)
}

export async function getDocs(ref) {
  const q = asQuery(ref)
  const res = await request("POST", "/db/query", wireQuery(q))
  return new QuerySnapshot(res.docs.map((d) => new DocumentSnapshot(new DocumentReference(q.collection, d.id), decode(d.data))))
}

export async function getCountFromServer(ref) {
  const res = await request("POST", "/db/query", { ...wireQuery(asQuery(ref)), count: true })
  return { data: () => ({ count: res.count }) }
}

// --- Writes -------------------------------------------------------------------

const write = (ops) => request("POST", "/db/write", { ops })

export async function addDoc(colRef, data) {
  const { ids } = await write([{ kind: "set", collection: colRef.id, data: encode(data) }])
  return new DocumentReference(colRef.id, ids[0])
}

export async function setDoc(ref, data, options = {}) {
  await write([{ kind: "set", collection: ref.collection, id: ref.id, data: encode(data), merge: !!options.merge }])
}

export async function updateDoc(ref, data) {
  await write([{ kind: "update", collection: ref.collection, id: ref.id, data: encode(data) }])
}

export async function deleteDoc(ref) {
  await write([{ kind: "delete", collection: ref.collection, id: ref.id }])
}

export function writeBatch() {
  const ops = []
  const batch = {
    set(ref, data, options = {}) {
      ops.push({ kind: "set", collection: ref.collection, id: ref.id, data: encode(data), merge: !!options.merge })
      return batch
    },
    update(ref, data) {
      ops.push({ kind: "update", collection: ref.collection, id: ref.id, data: encode(data) })
      return batch
    },
    delete(ref) {
      ops.push({ kind: "delete", collection: ref.collection, id: ref.id })
      return batch
    },
    commit: () => (ops.length ? write(ops) : Promise.resolve()),
  }
  return batch
}

// The guest view of an inspection report (the QR code on printed reports).
// Needs no login; the server returns only the fields meant for customers.
export async function getPublicReport(id) {
  try {
    const res = await request("GET", `/public/reports/${encodeURIComponent(id)}`)
    return { id: res.id, ...decode(res.data) }
  } catch (err) {
    if (err.status === 404) return null
    throw err
  }
}
